import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Rocket, CheckCircle, XCircle, AlertTriangle, Loader2, Play,
  ArrowRight, Shield, RefreshCw, ExternalLink, FileText, Globe,
  Zap, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_ICON = {
  pass: <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />,
  fail: <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />,
  warn: <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />,
  manual: <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />,
};

export default function LaunchReadinessPage() {
  const [checklist, setChecklist] = useState(null);
  const [smokeResults, setSmokeResults] = useState(null);
  const [health, setHealth] = useState(null);
  const [redirects, setRedirects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningSmoke, setRunningSmoke] = useState(false);
  const [tab, setTab] = useState('checklist');

  const loadData = useCallback(async () => {
    try {
      const [cl, h, r] = await Promise.all([
        api.get('/launch/checklist'),
        api.get('/launch/health'),
        api.get('/redirects/map'),
      ]);
      setChecklist(cl.data);
      setHealth(h.data);
      setRedirects(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runSmokeTests = async () => {
    setRunningSmoke(true);
    try {
      const r = await api.post('/launch/smoke-test');
      setSmokeResults(r.data);
      toast.success(`Smoke tests complete: ${r.data.summary.passed}/${r.data.summary.total} passed`);
    } catch { toast.error('Smoke tests failed'); }
    finally { setRunningSmoke(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const clSummary = checklist?.summary || {};

  return (
    <div data-testid="launch-readiness-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Launch Readiness</h1>
        </div>
        <Button size="sm" className="gap-1.5 text-xs" onClick={runSmokeTests} disabled={runningSmoke} data-testid="run-smoke-btn">
          {runningSmoke ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          {runningSmoke ? 'Running...' : 'Run Smoke Tests'}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Card className="bg-white border-stone/50"><CardContent className="p-3">
          <div className="flex items-center gap-1.5 mb-1"><CheckCircle className="h-3 w-3 text-emerald-600" /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Passed</span></div>
          <p className="text-xl font-semibold text-emerald-700">{clSummary.passed || 0}</p>
        </CardContent></Card>
        <Card className="bg-white border-stone/50"><CardContent className="p-3">
          <div className="flex items-center gap-1.5 mb-1"><Clock className="h-3 w-3 text-blue-600" /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Manual</span></div>
          <p className="text-xl font-semibold text-blue-700">{clSummary.manual || 0}</p>
        </CardContent></Card>
        <Card className="bg-white border-stone/50"><CardContent className="p-3">
          <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="h-3 w-3 text-amber-600" /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Warnings</span></div>
          <p className="text-xl font-semibold text-amber-700">{clSummary.warned || 0}</p>
        </CardContent></Card>
        <Card className="bg-white border-stone/50"><CardContent className="p-3">
          <div className="flex items-center gap-1.5 mb-1"><XCircle className="h-3 w-3 text-red-600" /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">Failed</span></div>
          <p className="text-xl font-semibold text-red-700">{clSummary.failed || 0}</p>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-9 mb-4">
          <TabsTrigger value="checklist" className="text-xs gap-1"><CheckCircle className="h-3 w-3" /> Owner Checklist</TabsTrigger>
          <TabsTrigger value="smoke" className="text-xs gap-1"><Zap className="h-3 w-3" /> Smoke Tests</TabsTrigger>
          <TabsTrigger value="providers" className="text-xs gap-1"><Shield className="h-3 w-3" /> Providers</TabsTrigger>
          <TabsTrigger value="redirects" className="text-xs gap-1"><Globe className="h-3 w-3" /> Redirects</TabsTrigger>
        </TabsList>

        {/* Owner Checklist */}
        <TabsContent value="checklist">
          <div className="space-y-3">
            {(checklist?.checks || []).map((check) => (
              <Card key={check.id} className={`bg-white border-stone/50 ${check.status === 'fail' ? 'border-l-4 border-l-red-400' : check.status === 'manual' ? 'border-l-4 border-l-blue-400' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {STATUS_ICON[check.status]}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-ink">{check.label}</span>
                        <Badge variant="outline" className="text-[9px]">{check.group}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{check.description}</p>
                      {check.detail && <p className="text-[11px] text-muted-foreground mt-1">{check.detail}</p>}
                      {check.action && (
                        <p className="text-[11px] mt-1.5 font-medium" style={{ color: '#1F5E7A' }}>{check.action}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Smoke Tests */}
        <TabsContent value="smoke">
          {!smokeResults ? (
            <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center">
              <Zap className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground mb-3">Run automated smoke tests to verify system readiness.</p>
              <Button size="sm" className="gap-1.5 text-xs" onClick={runSmokeTests} disabled={runningSmoke}>
                <Play className="h-3 w-3" /> Run Smoke Tests
              </Button>
            </CardContent></Card>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-emerald-600 font-medium">{smokeResults.summary.passed} passed</span>
                  {smokeResults.summary.warned > 0 && <span className="text-amber-600">{smokeResults.summary.warned} warnings</span>}
                  {smokeResults.summary.failed > 0 && <span className="text-red-600 font-medium">{smokeResults.summary.failed} failed</span>}
                  <span className="text-muted-foreground text-xs">{smokeResults.elapsed_seconds}s</span>
                </div>
                <Button size="sm" variant="outline" className="text-xs gap-1" onClick={runSmokeTests} disabled={runningSmoke}>
                  <RefreshCw className="h-3 w-3" /> Rerun
                </Button>
              </div>
              {/* Group by group */}
              {(() => {
                const groups = {};
                (smokeResults.results || []).forEach(r => { const g = r.group || 'Other'; if (!groups[g]) groups[g] = []; groups[g].push(r); });
                return Object.entries(groups).map(([group, tests]) => (
                  <Card key={group} className="bg-white border-stone/50 mb-3">
                    <CardContent className="p-3">
                      <h3 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{group}</h3>
                      <div className="space-y-1">
                        {tests.map((t, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                            {STATUS_ICON[t.status]}
                            <span className="text-ink">{t.test}</span>
                            {t.detail && <span className="text-muted-foreground ml-auto text-[10px] truncate max-w-[200px]">{t.detail}</span>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          )}
        </TabsContent>

        {/* Providers */}
        <TabsContent value="providers">
          <Card className="bg-white border-stone/50 mb-4">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Provider Status</h3>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-muted-foreground">Live providers:</span>
                <Badge variant="outline" className={`text-[10px] ${health?.allow_live ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                  {health?.allow_live ? 'ENABLED (caution)' : 'BLOCKED (safe)'}
                </Badge>
              </div>
              <div className="space-y-3">
                {health?.providers && Object.entries(health.providers).map(([key, p]) => (
                  <div key={key} className="flex items-start justify-between p-3 rounded-lg border border-stone/30" data-testid={`provider-row-${key}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-ink">{p.name}</span>
                        <Badge variant="outline" className={`text-[10px] ${p.is_activated ? 'bg-emerald-50 text-emerald-700' : 'bg-stone/30 text-muted-foreground'}`} data-testid={`provider-status-${key}`}>
                          {p.status_label || (p.is_activated ? 'LIVE' : 'Mock')}
                        </Badge>
                        {typeof p.imported_certificates === 'number' && (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200" data-testid="legacy-cert-count">
                            {p.imported_certificates} certificates imported
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">{p.docs}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {p.env_status && Object.entries(p.env_status).map(([k, v]) => (
                          <span key={k} className={`text-[9px] font-mono ${v ? 'text-emerald-600' : 'text-red-500'}`}>
                            {k}: {v ? 'set' : 'missing'}
                          </span>
                        ))}
                      </div>
                      {key === 'legacy_backend' && (
                        <a href="/team/imports" className="inline-flex items-center gap-1 text-[11px] mt-1.5 font-medium" style={{ color: '#1F5E7A' }} data-testid="legacy-backend-setup-link">
                          Configure connection &amp; run import <ArrowRight className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Seed Data Health</h3>
              {health?.seed_data && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(health.seed_data).map(([k, v]) => (
                    <div key={k} className="text-xs">
                      <span className="text-muted-foreground">{k.replace(/_/g, ' ')}</span>
                      <p className="font-medium text-ink">{v}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Redirects */}
        <TabsContent value="redirects">
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                  Redirect Map ({redirects?.total || 0} rules)
                </h3>
              </div>
              <div className="space-y-1 max-h-[500px] overflow-y-auto">
                {(redirects?.redirects || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-stone/10 last:border-0">
                    <span className="text-red-500 font-mono truncate w-[40%]">{r.source}</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-emerald-700 font-mono truncate w-[40%]">{r.target}</span>
                    <Badge variant="outline" className="text-[8px] flex-shrink-0">{r.status_code}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Documentation links */}
      <Card className="bg-white border-stone/50 mt-4">
        <CardContent className="p-4">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Launch Documentation</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { name: 'Owner Launch Checklist', file: 'owner-launch-checklist.md' },
              { name: 'Provider Activation', file: 'provider-activation-checklist.md' },
              { name: 'DNS Cutover Checklist', file: 'dns-cutover-checklist.md' },
              { name: 'Redirect Map', file: 'redirect-map.md' },
              { name: 'Rollback Notes', file: 'rollback-notes.md' },
              { name: 'Open Assumptions', file: 'open-assumptions.md' },
            ].map(doc => (
              <div key={doc.file} className="flex items-center gap-1.5 text-xs text-muted-foreground py-1">
                <FileText className="h-3 w-3 flex-shrink-0" />
                <span>{doc.name}</span>
                <span className="text-[9px] font-mono ml-auto">{doc.file}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
